import ApiService from './apiService'

class SupplierInvoiceService {
  /**
   * Get list of supplier invoices
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  getSupplierInvoices(params = {}) {
    return ApiService.get('/supplier-invoices', params)
  }

  /**
   * Get single supplier invoice with details
   * @param {number} id - Invoice ID
   * @returns {Promise}
   */
  getSupplierInvoice(id) {
    return ApiService.get(`/supplier-invoices/${id}`)
  }

  /**
   * Create new supplier invoice from purchase orders
   * @param {Object} data - Invoice data
   * @returns {Promise}
   */
  createSupplierInvoice(data) {
    return ApiService.post('/supplier-invoices', data)
  }

  /**
   * Update supplier invoice
   * @param {number} id - Invoice ID
   * @param {Object} data - Update data
   * @returns {Promise}
   */
  updateSupplierInvoice(id, data) {
    return ApiService.put(`/supplier-invoices/${id}`, data)
  }

  /**
   * Delete supplier invoice
   * @param {number} id - Invoice ID
   * @returns {Promise}
   */
  deleteSupplierInvoice(id) {
    return ApiService.delete(`/supplier-invoices/${id}`)
  }

  /**
   * Add payment to supplier invoice
   * @param {number} id - Invoice ID
   * @param {Object} paymentData - Payment data
   * @returns {Promise}
   */
  addPayment(id, paymentData) {
    return ApiService.post(`/supplier-invoices/${id}/payments`, paymentData)
  }

  /**
   * Get payments for an invoice
   * @param {number} id - Invoice ID
   * @returns {Promise}
   */
  getPayments(id) {
    return ApiService.get(`/supplier-invoices/${id}/payments`)
  }

  /**
   * Delete a payment
   * @param {number} invoiceId - Invoice ID
   * @param {number} paymentId - Payment ID
   * @returns {Promise}
   */
  deletePayment(invoiceId, paymentId) {
    return ApiService.delete(`/supplier-invoices/${invoiceId}/payments/${paymentId}`)
  }

  /**
   * Download invoice PDF
   * @param {number} id - Invoice ID
   * @returns {Promise}
   */
  downloadSupplierInvoicePdf(id) {
    return ApiService.download(`/supplier-invoices/${id}/download`, {}, `facture-fournisseur-${id}.pdf`)
  }

  /**
   * Get invoice statistics
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  getStats(params = {}) {
    return ApiService.get('/supplier-invoices/stats', params)
  }

  /**
   * Utility: Format currency
   * @param {number} amount - Amount to format
   * @returns {string}
   */
  formatCurrency(amount) {
    if (!amount && amount !== 0) return '-'
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount)
  }

  /**
   * Utility: Format date
   * @param {string} dateString - Date string
   * @returns {string}
   */
  formatDate(dateString) {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR')
  }

  /**
   * Utility: Get status label
   * @param {string} status - Status code
   * @returns {string}
   */
  getStatusLabel(status) {
    const labels = {
      'pending': 'En attente',
      'paid': 'Payée',
      'partial_paid': 'Partiellement payée',
      'cancelled': 'Annulée',
      'overdue': 'En retard'
    }
    return labels[status] || status
  }

  /**
   * Utility: Get payment method label
   * @param {string} method - Payment method code
   * @returns {string}
   */
  getPaymentMethodLabel(method) {
    const labels = {
      'cash': 'Espèces',
      'check': 'Chèque',
      'bank_transfer': 'Virement bancaire',
      'credit_card': 'Carte bancaire',
      'mobile_money': 'Mobile Money',
      'other': 'Autre'
    }
    return labels[method] || method
  }
}

export default new SupplierInvoiceService()

